import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as suppliersApi from './suppliers.api';

import type {
  CreateSupplierRequest,
  SupplierBulkRecategorizeRequest,
  SupplierListQuery,
  UpdateSupplierRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: SupplierListQuery) => ['suppliers', 'list', query] as const,
  detail: (id: string) => ['suppliers', 'detail', id] as const,
  currencyOptions: ['currencies', 'options'] as const,
};

export const useSuppliersList = (query: SupplierListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => suppliersApi.listSuppliers(query),
    staleTime: 30_000,
  });

/** Full record for the edit drawer - list rows lack address, tax ids, documents. */
export const useSupplierDetail = (id: string | null) =>
  useQuery({
    queryKey: QK.detail(id ?? ''),
    queryFn: () => suppliersApi.getSupplier(id ?? ''),
    enabled: id !== null,
  });

export const useCreateSupplier = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSupplierRequest) => suppliersApi.createSupplier(payload),
    onSuccess: (supplier) => {
      qc.invalidateQueries({ queryKey: ['suppliers', 'list'] });
      toast.success(`Added ${supplier.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add supplier'),
  });
};

export const useUpdateSupplier = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateSupplierRequest }) =>
      suppliersApi.updateSupplier(id, payload),
    onSuccess: (supplier) => {
      qc.invalidateQueries({ queryKey: ['suppliers', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(supplier.id) });
      toast.success('Supplier updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update supplier'),
  });
};

export const useDeleteSupplier = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => suppliersApi.deleteSupplier(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['suppliers', 'list'] });
      toast.success('Supplier deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete supplier'),
  });
};

export const useCurrencyOptions = () =>
  useQuery({
    queryKey: QK.currencyOptions,
    queryFn: () => suppliersApi.listCurrencyOptions(),
    staleTime: 5 * 60_000,
  });

export const useBulkDeleteSuppliers = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => suppliersApi.bulkDeleteSuppliers(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppliers', 'list'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete suppliers'),
  });
};

export const useBulkSetSuppliersStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      suppliersApi.bulkSetSuppliersStatus(ids, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppliers', 'list'] }),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update supplier status'),
  });
};

export const useBulkRecategorizeSuppliers = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SupplierBulkRecategorizeRequest) =>
      suppliersApi.bulkRecategorizeSuppliers(payload),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['suppliers', 'list'] });
      toast.success(`Recategorized ${result.updated} supplier${result.updated === 1 ? '' : 's'}`);
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to recategorize suppliers'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
